import type {DataRouterApplicationBuilder} from '@github-ui/react-core/future/data-router-application'
import type {RouteObject} from '@github-ui/react-router'
import type {ComponentType} from 'react'

import {workflowRouteParam} from '../workflow-path'
import {buildAllWorkflowRunsRoute} from './all-workflow-runs-route'
import {buildWorkflowRunsRoute} from './workflow-runs-route'
import type {ActionsShellRouteHandle} from './actions-shell-route-handle'
import {buildWorkflowsLayoutRoute} from './workflows-layout-route'
import {createActionsRailsFragmentRoutes, ActionsRailsFragmentErrorBoundary} from './actions-rails-fragment-routes'
import {buildActionsRunnersRoute} from './actions-runners-route'
import {buildActionsCachesRoute} from './actions-caches-route'

export interface ActionsWorkflowsRouteComponents {
  AllWorkflowRuns: ComponentType
  AllWorkflowRunsError: ComponentType
  CachesRoute: ComponentType
  RepositoryRunners: ComponentType
  WorkflowRuns: ComponentType
  WorkflowRunsError: ComponentType
  WorkflowsLayout: ComponentType
}

const allWorkflowsShell = {
  kind: 'workflow',
  title: 'All workflows',
  getSelection: () => ({type: 'all-workflows'}),
} satisfies ActionsShellRouteHandle

const workflowRunsShell = {
  kind: 'workflow',
  title: 'Workflow runs',
  getSelection: params => ({type: 'workflow', filename: workflowRouteParam(params)}),
} satisfies ActionsShellRouteHandle

const cachesShell = {
  kind: 'native',
  title: 'Caches',
  getSelection: () => ({type: 'caches'}),
} satisfies ActionsShellRouteHandle

const runnersShell = {
  kind: 'native',
  title: 'Runners',
  getSelection: () => ({type: 'runners'}),
} satisfies ActionsShellRouteHandle

/** Builds the Actions Workflows routes for either the standalone or repo app builder. */
export function createActionsWorkflowsRoutes(
  appBuilder: DataRouterApplicationBuilder<string>,
  components: ActionsWorkflowsRouteComponents,
  {nativeCachesEnabled = false}: {nativeCachesEnabled?: boolean} = {},
): RouteObject[] {
  const {
    AllWorkflowRuns,
    AllWorkflowRunsError,
    CachesRoute,
    RepositoryRunners,
    WorkflowRuns,
    WorkflowRunsError,
    WorkflowsLayout,
  } = components
  const cachesRoute = buildActionsCachesRoute(appBuilder)
  const runnersRoute = buildActionsRunnersRoute(appBuilder)
  const CachesErrorBoundary = () => <ActionsRailsFragmentErrorBoundary appName={appBuilder.name} />

  return [
    buildWorkflowsLayoutRoute(appBuilder).toRoute({
      Component: WorkflowsLayout,
      children: [
        buildAllWorkflowRunsRoute(appBuilder).toRoute({
          Component: AllWorkflowRuns,
          handle: {actionsShell: allWorkflowsShell},
          errorBoundary: {fallback: <AllWorkflowRunsError />},
        }),
        buildWorkflowRunsRoute(appBuilder).toRoute({
          Component: WorkflowRuns,
          handle: {actionsShell: workflowRunsShell},
          errorBoundary: {fallback: <WorkflowRunsError />},
        }),
        ...(nativeCachesEnabled
          ? [
              cachesRoute.toRoute({
                Component: CachesRoute,
                handle: {actionsShell: cachesShell},
                errorBoundary: {override: CachesErrorBoundary},
              }),
            ]
          : []),
        runnersRoute.toRoute({
          Component: RepositoryRunners,
          handle: {actionsShell: runnersShell},
        }),
        ...createActionsRailsFragmentRoutes(appBuilder, {nativeCachesEnabled}),
      ],
    }),
  ]
}
